export enum OrderStatusEnum {
  RECEIVED = 'order.received',
  SCHEDULED = 'appointment.scheduled',
  PROCESSING = 'order.post_processing',
  VERIFIED = 'order.verified',
  DELIVERED = 'order.delivered',
  PROCESSED = 'order.processed',
  CANCELLED = 'order.canceled',
  UNABLE_TO_COMPLETE = 'order.unable_to_complete',
  APPOINTMENT_COMPLETE = 'appointment.complete',
  AWAITING_RESCHEDULE = 'appointment.awaiting_reschedule'
}

/**
 * "The photos are with the customer." VERIFIED counts as delivered: it is a
 * post-delivery QA state, not a step before it.
 *
 * Extracted (DV-2820) because this two-value test was being written out by hand
 * at each call site. OrderCellAssets, OrderCell (via toDisplayStatusKey) and the
 * pre-delivery upload gate use it; propertyListUtils still carries its own copy
 * and should adopt this when next touched.
 */
export const isDeliveredOrderStatus = (
  status: string | null | undefined
): boolean =>
  status === OrderStatusEnum.DELIVERED || status === OrderStatusEnum.VERIFIED;

/**
 * Collapses intermediate backend states into the user-facing milestone the
 * Order Status cell shows: VERIFIED reads as DELIVERED, PROCESSED as RECEIVED.
 * The cell's title and progress dots must both read this key, never the raw
 * status, so they stay in lockstep.
 */
export const toDisplayStatusKey = (status: string): string => {
  if (isDeliveredOrderStatus(status)) return OrderStatusEnum.DELIVERED;
  if (status === OrderStatusEnum.PROCESSED) return OrderStatusEnum.RECEIVED;
  return status;
};

/**
 * Pipeline-stage colours for the <Status /> bar SVG (32×6 px indicators
 * rendered four-wide under each row in the order list).
 *
 * Intentionally NOT mapped onto the design-system success / destructive /
 * warning tokens. At indicator scale, vivid saturation reads as state
 * far more clearly than the muted palette tuned for buttons and text.
 */
export enum OrderStatusColors {
  GREEN = '#00C31F',
  GRAY = '#D9D9D9',
  RED = '#FF0000',
  YELLOW = '#feb72a'
}

export type OrderStatus = {
  [OrderStatusEnum.RECEIVED]: boolean;
  [OrderStatusEnum.PROCESSED]: boolean;
  [OrderStatusEnum.CANCELLED]: boolean | null;
  [OrderStatusEnum.SCHEDULED]: boolean;
  [OrderStatusEnum.AWAITING_RESCHEDULE]: boolean | undefined;
  [OrderStatusEnum.APPOINTMENT_COMPLETE]: boolean;
  [OrderStatusEnum.PROCESSING]: boolean;
  [OrderStatusEnum.VERIFIED]: boolean;
  [OrderStatusEnum.DELIVERED]: boolean;
  [OrderStatusEnum.UNABLE_TO_COMPLETE]: boolean | null;
};
